import { useNavigate, useParams } from 'react-router-dom';
import { PageHeader } from '../ui/PageHeader';
import { MissingItem } from '../ui/MissingItem';
import { Panel } from '../ui/Panel';
import { Field } from '../ui/Field';
import { ImageField } from '../ui/ImageField';
import { ProductPicker } from '../ui/ProductPicker';
import { ContentEditor } from '../ui/ContentEditor';
import { DeleteButton } from '../ui/DeleteButton';
import { undoToast } from '../ui/undoToast';
import { NumberInput } from '../ui/NumberInput';
import { CheckList } from '../ui/CheckList';
import { ViewOnSite } from './ViewOnSite';
import { useEditorItem } from './useEditorItem';
import { deleteBlockedReason, routineLinks } from './catalogLinks';
import { useAdmin } from '../AdminContext';
import { SeoPanel } from '../ui/SeoPanel';
import { routineSeoChecks } from './catalogSeo';
import { routineSeoAuto } from '../../utils/seoAuto';
import { nextTierIn, tierIn } from '../../data/gifts';
import { skinTypes } from '../../data/concerns';
import { emptyRoutine, oneOf, routineChecks, routineLevels } from './catalogRules';
import { btnPrimary, inputCls } from '../ui/styles';
import { slugify, uniqueSlug } from '../editing';
import { euro } from '../../core/format/currency';
const back = {
  to: '/be/protocollen',
  label: 'Alle protocollen'
};
const levelLabel: Record<(typeof routineLevels)[number], string> = {
  Start: 'Startprotocol (3–4 producten)',
  Compleet: 'Compleet protocol'
};

/** "Wat erbij zit" komt als lijst uit de ContentEditor; alleen tekstregels tellen. */
const asLines = (value: unknown): string[] => Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : [];

/** Eén protocol: verhaal, producten in volgorde, wat erbij zit en de prijs (automatisch). */
export function RoutineEditor() {
  const {
    id = ''
  } = useParams();
  const navigate = useNavigate();
  const {
    catalog,
    index,
    site,
    putItem,
    removeItem
  } = useAdmin();
  const {
    isNew,
    item: routine,
    set
  } = useEditorItem('routines', id, emptyRoutine);
  if (!routine) return <MissingItem title="Protocol niet gevonden" back={back} data-id="element-842" data-mp="4ujl6ifdbrd5" />;
  const totals = index.routineTotals(routine);
  // De staffel uit het concept: een wijziging in Instellingen is hier meteen te zien.
  const gift = tierIn(site.giftTiers, totals.price);
  const next = nextTierIn(site.giftTiers, totals.price);
  const covered = skinTypes.filter(s => totals.items.some(p => p.skinTypes.includes(s)));
  const usedBy = routineLinks(catalog, routine.id);
  const add = () => {
    const created = {
      ...routine,
      id: uniqueSlug(slugify(routine.name), catalog.routines.map(r => r.id))
    };
    putItem('routines', created);
    navigate(`/be/protocollen/${created.id}`, {
      replace: true
    });
  };
  return <div data-id="element-843" data-mp="8vvccsdw4kng">
      <PageHeader title={isNew ? 'Nieuw protocol' : routine.name} back={back} actions={isNew ? <button type="button" onClick={add} disabled={!routine.name.trim()} className={btnPrimary} data-id="element-845" data-mp="16ckb2d7jn6sm">
              Protocol toevoegen
            </button> : <>
              <DeleteButton undoable blockedReason={deleteBlockedReason(usedBy, 'Kies daar eerst een ander protocol.')} onConfirm={() => {
        const restore = removeItem('routines', routine.id);
        navigate(back.to);
        undoToast(`“${routine.name || 'Naamloos protocol'}” verwijderd`, restore, {
          description: 'Pas definitief als je opslaat.',
          restored: 'Protocol teruggezet'
        });
      }} data-id="element-846" data-mp="19uhvm3502oaj" />
              <ViewOnSite path="/routines" data-id="element-847" data-mp="1wqrt6phuyg8g" />
            </>} data-id="element-844" data-mp="8ubyf8fpilou" />

      <div className="grid gap-6 lg:grid-cols-12" data-id="element-848" data-mp="21kcw8vyb60ei">
        <div className="space-y-6 lg:col-span-8" data-id="element-849" data-mp="26j7bjnx2h6tv">
          <Panel title="Over dit protocol" data-id="element-850" data-mp="26hw7dmzplx28">
            <div className="grid gap-5 sm:grid-cols-2" data-id="element-851" data-mp="2biaixf0irp50">
              <Field label="Naam" className="sm:col-span-2" data-id="element-852" data-mp="2bfo0gkc3bp80">
                <input value={routine.name} onChange={e => set({
                name: e.target.value
              })} className={inputCls} data-id="element-853" data-mp="2ghwxbrs33bfe" />
              </Field>
              <Field label="Kernzin" hint="Eén zin op de protocolkaart." className="sm:col-span-2" data-id="element-854" data-mp="3d65s9f1galxr">
                <input value={routine.claim} onChange={e => set({
                claim: e.target.value
              })} className={inputCls} data-id="element-855" data-mp="3871l0ty36ya2" />
              </Field>
              <Field label="Voor wie" className="sm:col-span-2" data-id="element-856" data-mp="3hyfeajzg31ur">
                <input value={routine.forWho} onChange={e => set({
                forWho: e.target.value
              })} className={inputCls} data-id="element-857" data-mp="3i11u16whono2" />
              </Field>
              <Field label="Het verhaal" hint="Waarom deze producten samen werken." className="sm:col-span-2" data-id="element-858" data-mp="2pateznx92jdp">
                <textarea value={routine.story} onChange={e => set({
                story: e.target.value
              })} rows={4} className={`${inputCls} resize-y leading-relaxed`} data-id="element-859" data-mp="33b42qi39jhun" />
              </Field>
              <Field label="Soort" data-id="element-860" data-mp="ijan3vzyjd5w">
                <select value={routine.level} onChange={e => {
                const level = oneOf(routineLevels, e.target.value);
                if (level) set({
                  level
                });
              }} className={inputCls} data-id="element-861" data-mp="ii8zks1r8y7a">
                  {routineLevels.map(l => <option key={l} value={l} data-id="element-862" data-mp="c3ldjs7jmwj9">
                      {levelLabel[l]}
                    </option>)}
                </select>
              </Field>
              <Field label="Hoort bij behandeling" hint="Wordt aangeboden na die afspraak." data-id="element-863" data-mp="h4r71s912ntu">
                <select value={routine.treatmentSlug ?? ''} onChange={e => set({
                treatmentSlug: e.target.value || undefined
              })} className={inputCls} data-id="element-864" data-mp="14uc0x66pz5hw">
                  <option value="" data-id="element-865" data-mp="v37mc7kyhmnl">Geen</option>
                  {catalog.treatments.map(t => <option key={t.slug} value={t.slug} data-id="element-866" data-mp="100r3bf91gdj8">
                      {t.name}
                    </option>)}
                </select>
              </Field>
              <Field label="Gaat minimaal mee (maanden)" data-id="element-867" data-mp="1sid4e0p1jvqg">
                <NumberInput value={routine.lasts.minMonths} onChange={v => v !== undefined && set({
                lasts: {
                  ...routine.lasts,
                  minMonths: v
                }
              })} min={1} max={24} data-id="element-868" data-mp="1sjwlftiiuata" />
              </Field>
              <Field label="Gaat maximaal mee (maanden)" hint={routine.lasts.maxMonths < routine.lasts.minMonths ? 'Lager dan het minimum: de site toont dan alleen het minimum.' : undefined} data-id="element-869" data-mp="1tbszyhe46flp">
                <NumberInput value={routine.lasts.maxMonths} onChange={v => v !== undefined && set({
                lasts: {
                  ...routine.lasts,
                  maxMonths: v
                }
              })} min={1} max={24} data-id="element-870" data-mp="1eo7uigtro4am" />
              </Field>
            </div>
          </Panel>

          <Panel title="Producten, in volgorde van gebruik" description="Reinigen → toner → serum → crème → SPF. Verplaats met de pijltjes." data-id="element-871" data-mp="10o6v75t5h4x9">
            <ProductPicker value={routine.productIds} onChange={productIds => set({
            productIds
          })} products={catalog.products} ordered data-id="element-872" data-mp="vqoh7109zzie" />
          </Panel>

          <Panel title="Wat er zonder extra kosten bij zit" description="Nooit een korting — wel begeleiding en papier." data-id="element-873" data-mp="3cbjtpccy0qh1">
            <ContentEditor value={routine.included} onChange={v => set({
            included: asLines(v)
          })} fieldKey="included" data-id="element-874" data-mp="3hamuulpsycjq" />
          </Panel>

          <SeoPanel seo={routine.seo} onChange={seo => set({
          seo
        })} auto={routineSeoAuto(routine)} path={null} imageFallback="het sfeerbeeld" data-id="element-875" data-mp="3ladwjkgvad31" />
        </div>

        <aside aria-label="Protocolinstellingen" className="space-y-6 lg:col-span-4" data-id="element-876" data-mp="2tmdieaehb159">
          <Panel title="Prijs" data-id="element-877" data-mp="2xmnmfhipnhhh">
            <p className="font-display text-4xl font-light text-ink" data-id="element-878" data-mp="2xlckln8tsjt0">{euro(totals.price)}</p>
            <p className="mt-1 text-[13px] text-muted" data-id="element-879" data-mp="32efc7pu1lj6k">Som van {totals.items.length} producten, automatisch.</p>
            <p className="mt-4 border-t border-line pt-4 text-[13px] leading-relaxed text-muted" data-id="element-880" data-mp="25vhh8mo4utlc">
              {gift ? <>
                  Klant krijgt: <span className="text-ink" data-id="element-881" data-mp="1397et6x7qezq">{gift.title}</span>
                </> : 'Nog geen cadeautrede bereikt.'}
              {next && <>
                  <br data-id="element-882" data-mp="1d97ywv2woz4z" />
                  Nog {euro(next.threshold - totals.price)} tot {next.phrase}.
                </>}
            </p>
          </Panel>

          <Panel title="Huidthema’s die dit dekt" data-id="element-883" data-mp="1m8ldg25w0dwk">
            <p className="text-[13px] leading-relaxed text-muted" data-id="element-884" data-mp="1m7a5rr9bmm06">{covered.length ? covered.join(', ') : 'Nog geen — voeg producten toe.'}</p>
            {usedBy.length > 0 && <p className="mt-3 text-[13px] text-muted" data-id="element-885" data-mp="1vxczjto9o3zk">
                Aanbevolen bij: <span className="text-ink" data-id="element-886" data-mp="1qz1s1plg25ki">{usedBy.map(c => c.label).join(', ')}</span>
              </p>}
          </Panel>

          <Panel title="Sfeerbeeld" data-id="element-887" data-mp="24zvza4wra0hl">
            <ImageField value={routine.image} onChange={image => set({
            image
          })} fit="cover" aspect="aspect-[4/5]" data-id="element-888" data-mp="20xrapkl5zu1t" />
          </Panel>

          <Panel title="Wat mist er nog?" data-id="element-889" data-mp="2jn4hvqse983l">
            <CheckList checks={[...routineChecks(routine), ...routineSeoChecks(routine, catalog.routines)]} okText="Dit protocol is compleet" data-id="element-890" data-mp="2jjypa1kk3o34" />
          </Panel>
        </aside>
      </div>
    </div>;
}